import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { appConfirmMessage } from '../utils/appConfirm';
import { FaEye, FaSearch, FaTrash } from 'react-icons/fa';
import { toast } from 'react-toastify';
import '../styles/InvoicesList.css';
import { graphqlRequest } from '../utils/graphqlRequest';
import { getInvoiceDisplayStatusMeta } from '../utils/paymentStatus';
import { formatAUD } from '../utils/formatCurrency';
import ViewInvoice from './ViewInvoice';
import { useAdminPageSearch } from '../utils/useAdminPageSearch';

const FETCH_ALL_BOOKING_INVOICES = `
  query FindAllBookingInvoice {
    findAllBookingInvoice {
      id
      invoiceNumber
      paymentStatus
      bookingId
      projectId
      createdAt
      invoiceStage
      listCustomerName
      listCustomerEmail
      listProjectCode
      listProjectName
      listServiceCount
      listTotal
      listInvoiceKind
    }
  }
`;

const FETCH_INVOICE_DETAIL = `
  query FindOneBookingInvoice($id: String!) {
    findOneBookingInvoice(id: $id) {
      id
      invoiceNumber
      paymentStatus
      bookingId
      projectId
      invoiceStage
      createdAt
      rawData
    }
  }
`;

const DELETE_INVOICE = `
  mutation DeleteInvoice($invoiceId: String!) {
    deleteInvoice(invoiceId: $invoiceId)
  }
`;

function isCustomInvoice(inv) {
  return String(inv?.listInvoiceKind || '').toUpperCase() === 'CUSTOM';
}

function formatInvoiceDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function toViewInvoiceShape(detail) {
  const raw = detail?.rawData || {};
  return {
    ...raw,
    id: detail.id,
    invoiceNumber: detail.invoiceNumber,
    paymentStatus: detail.paymentStatus,
    invoiceStage: detail.invoiceStage,
    createdAt: detail.createdAt,
    projectId: detail.projectId || raw.projectId || null,
    bookingInfo: {
      ...(raw.bookingInfo || {}),
      id: detail.bookingId || raw.bookingInfo?.id,
    },
  };
}

export default function AllInvoices() {
  const { get, patch } = useAdminPageSearch();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showViewInvoice, setShowViewInvoice] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [kindFilter, setKindFilter] = useState('all');
  const [query, setQuery] = useState('');
  const restoredInvoiceRef = useRef('');
  const invoiceFromUrl = get('view');

  const loadInvoices = useCallback(async () => {
    try {
      setLoading(true);
      const res = await graphqlRequest(FETCH_ALL_BOOKING_INVOICES);
      setInvoices(res.findAllBookingInvoice || []);
    } catch (err) {
      console.error(err);
      toast.error(err?.message || 'Failed to load invoices');
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadInvoices();
  }, [loadInvoices]);

  const openInvoiceDetail = async (inv) => {
    if (!inv?.id) return;
    setLoadingDetail(true);
    try {
      const res = await graphqlRequest(FETCH_INVOICE_DETAIL, { id: inv.id });
      const detail = res.findOneBookingInvoice;
      if (!detail) {
        toast.error('Invoice not found');
        restoredInvoiceRef.current = '';
        patch({}, ['view']);
        return;
      }
      restoredInvoiceRef.current = detail.id;
      setSelectedInvoice(toViewInvoiceShape(detail));
      setShowViewInvoice(true);
      patch({ view: detail.id });
    } catch (err) {
      console.error(err);
      restoredInvoiceRef.current = '';
      toast.error(err?.message || 'Failed to load invoice details');
    } finally {
      setLoadingDetail(false);
    }
  };

  useEffect(() => {
    if (!invoiceFromUrl) return;
    if (restoredInvoiceRef.current === invoiceFromUrl) return;
    restoredInvoiceRef.current = invoiceFromUrl;
    openInvoiceDetail({ id: invoiceFromUrl });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceFromUrl]);

  const handleDelete = async (inv) => {
    const label = inv.invoiceNumber || inv.id;
    if (
      !await appConfirmMessage(
        `Delete invoice ${label}? This cannot be undone from the list.`,
      )
    ) {
      return;
    }
    setDeletingId(inv.id);
    try {
      await graphqlRequest(DELETE_INVOICE, { invoiceId: inv.id });
      toast.success(`Invoice ${label} deleted`);
      if (selectedInvoice?.id === inv.id) {
        restoredInvoiceRef.current = '';
        setShowViewInvoice(false);
        setSelectedInvoice(null);
        patch({}, ['view']);
      }
      await loadInvoices();
    } catch (err) {
      console.error(err);
      toast.error(err?.message || 'Failed to delete invoice');
    } finally {
      setDeletingId(null);
    }
  };

  const counts = useMemo(() => {
    const custom = invoices.filter(isCustomInvoice).length;
    return {
      all: invoices.length,
      custom,
      regular: invoices.length - custom,
    };
  }, [invoices]);

  const visibleInvoices = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return invoices.filter((inv) => {
      const custom = isCustomInvoice(inv);
      if (kindFilter === 'custom' && !custom) return false;
      if (kindFilter === 'regular' && custom) return false;
      if (!needle) return true;
      const haystack = [
        inv.invoiceNumber,
        inv.listCustomerName,
        inv.listCustomerEmail,
        inv.listProjectCode,
        inv.listProjectName,
        inv.invoiceStage,
        inv.paymentStatus,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(needle);
    });
  }, [invoices, kindFilter, query]);

  const emptyMessage = !invoices.length
    ? 'No invoices found.'
    : kindFilter === 'custom'
      ? 'No custom invoices match this view.'
      : kindFilter === 'regular'
        ? 'No regular invoices match this view.'
        : 'No invoices match this search.';

  return (
    <div className="ai-page">
      <header className="ai-header">
        <div>
          <h2 className="ai-title">All Invoices</h2>
          <p className="ai-subtitle">
            Regular visit bills and custom extra-job invoices across projects.
          </p>
        </div>
      </header>

      <div className="ai-panel">
        <div className="ai-toolbar">
          <div className="ai-kind-filter" role="tablist" aria-label="Invoice type">
            {[
              { id: 'all', label: 'All', count: counts.all },
              { id: 'regular', label: 'Regular', count: counts.regular },
              { id: 'custom', label: 'Custom', count: counts.custom },
            ].map((opt) => (
              <button
                key={opt.id}
                type="button"
                role="tab"
                aria-selected={kindFilter === opt.id}
                className={`ai-kind-btn${kindFilter === opt.id ? ' is-active' : ''}`}
                onClick={() => setKindFilter(opt.id)}
              >
                {opt.label}
                <span className="ai-kind-count">{opt.count}</span>
              </button>
            ))}
          </div>
          <label className="ai-search">
            <FaSearch aria-hidden />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search invoice, client or project"
            />
          </label>
        </div>

        {loading ? (
          <p className="ai-empty">Loading invoices…</p>
        ) : !visibleInvoices.length ? (
          <p className="ai-empty">{emptyMessage}</p>
        ) : (
          <div className="ai-table-wrap">
            <table className="ai-table">
              <thead>
                <tr>
                  <th>Invoice</th>
                  <th>Type</th>
                  <th>Client</th>
                  <th>Project</th>
                  <th>Date</th>
                  <th>Total</th>
                  <th>Payment</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {visibleInvoices.map((inv) => {
                  const statusMeta = getInvoiceDisplayStatusMeta(inv);
                  const custom = isCustomInvoice(inv);
                  const busy = loadingDetail || deletingId === inv.id;
                  return (
                    <tr
                      key={inv.id}
                      className={custom ? 'ai-row--custom' : undefined}
                    >
                      <td>
                        <strong className="ai-invoice-no">
                          {inv.invoiceNumber || '—'}
                        </strong>
                        <span className="ai-muted">
                          {inv.invoiceStage || 'FINAL'}
                          {inv.listServiceCount
                            ? ` · ${inv.listServiceCount} service${
                                inv.listServiceCount === 1 ? '' : 's'
                              }`
                            : ''}
                        </span>
                      </td>
                      <td>
                        <span
                          className={`ai-kind-badge ${
                            custom ? 'ai-kind-badge--custom' : 'ai-kind-badge--regular'
                          }`}
                        >
                          {custom ? 'Custom' : 'Regular'}
                        </span>
                      </td>
                      <td>
                        <span className="ai-primary-text">
                          {inv.listCustomerName || '—'}
                        </span>
                        {inv.listCustomerEmail ? (
                          <span className="ai-muted">{inv.listCustomerEmail}</span>
                        ) : null}
                      </td>
                      <td>
                        <span className="ai-primary-text">
                          {inv.listProjectCode || inv.listProjectName || '—'}
                        </span>
                        {inv.listProjectCode && inv.listProjectName ? (
                          <span className="ai-muted">{inv.listProjectName}</span>
                        ) : null}
                      </td>
                      <td>{formatInvoiceDate(inv.createdAt)}</td>
                      <td className="ai-amount">{formatAUD(inv.listTotal ?? 0)}</td>
                      <td>
                        <span className={`ai-status ${statusMeta.className}`}>
                          {statusMeta.label}
                        </span>
                      </td>
                      <td className="ai-actions">
                        <div className="ai-actions-row">
                          <button
                            type="button"
                            className="ai-action-btn"
                            disabled={busy}
                            onClick={() => openInvoiceDetail(inv)}
                            title="View invoice"
                            aria-label="View invoice"
                          >
                            <FaEye />
                          </button>
                          <button
                            type="button"
                            className="ai-action-btn ai-action-btn--delete"
                            disabled={busy}
                            onClick={() => handleDelete(inv)}
                            title={
                              deletingId === inv.id
                                ? 'Deleting…'
                                : 'Delete invoice'
                            }
                            aria-label="Delete invoice"
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showViewInvoice && selectedInvoice && (
        <ViewInvoice
          invoice={selectedInvoice}
          bookingId={selectedInvoice.bookingInfo?.id || selectedInvoice.bookingId}
          onClose={() => {
            restoredInvoiceRef.current = '';
            setShowViewInvoice(false);
            setSelectedInvoice(null);
            patch({}, ['view']);
            loadInvoices();
          }}
          onPaymentStatusChange={() => {
            loadInvoices();
          }}
        />
      )}
    </div>
  );
}
